import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import {
  Eye,
  TrendingUp,
  Users,
  FileText,
  Phone,
  Mail,
  BarChart3
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Agents = () => {
  const [agents, setAgents] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();

  useEffect(() => {
    fetchAgents();
    fetchStats();
  }, []);

  const fetchAgents = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/users?ruolo=sub_agent`);
      setAgents(response.data);
    } catch (error) {
      console.error('Error fetching agents:', error);
      toast.error('Errore nel caricamento dei sub-agenti');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/dashboard/stats?periodo=mese`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const getAgentStats = (agentId) => {
    if (!stats?.performance_agenti) return null;
    return stats.performance_agenti.find(a => a.id === agentId);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('it-IT');
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-48" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(6)].map((_, i) => (
            <Skeleton key={i} className="h-48" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="agents-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Sub-agenti</h1>
        <p className="text-slate-500 mt-1">{agents.length} sub-agenti attivi</p>
      </div>

      {/* Performance Summary */}
      {stats?.performance_agenti && stats.performance_agenti.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <BarChart3 className="w-5 h-5" />
              Classifica Performance (Ultimo Mese)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="table-header">
                    <th className="text-left px-4 py-3">Posizione</th>
                    <th className="text-left px-4 py-3">Agente</th>
                    <th className="text-right px-4 py-3">Lead</th>
                    <th className="text-right px-4 py-3">Convertiti</th>
                    <th className="text-right px-4 py-3">Contratti</th>
                    <th className="text-right px-4 py-3">Tasso Conv.</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.performance_agenti.slice(0, 5).map((agent, index) => (
                    <tr key={agent.id} className="table-row">
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold ${
                          index === 0 ? 'bg-amber-100 text-amber-700' :
                          index === 1 ? 'bg-slate-200 text-slate-700' :
                          index === 2 ? 'bg-orange-100 text-orange-700' :
                          'bg-slate-100 text-slate-600'
                        }`}>
                          {index + 1}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-medium text-slate-900">{agent.nome}</td>
                      <td className="px-4 py-3 text-right tabular-nums text-slate-600">{agent.lead_totali}</td>
                      <td className="px-4 py-3 text-right tabular-nums text-slate-600">{agent.lead_convertiti}</td>
                      <td className="px-4 py-3 text-right tabular-nums font-semibold text-slate-900">
                        {agent.contratti_chiusi}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${
                          agent.tasso_conversione >= 50 ? 'bg-emerald-100 text-emerald-700' :
                          agent.tasso_conversione >= 25 ? 'bg-amber-100 text-amber-700' :
                          'bg-slate-100 text-slate-600'
                        }`}>
                          {agent.tasso_conversione}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Agents Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {agents.length === 0 ? (
          <div className="col-span-full text-center py-12 text-slate-500">
            Nessun sub-agente trovato
          </div>
        ) : (
          agents.map((agent) => {
            const agentStats = getAgentStats(agent.id);
            return (
              <Card 
                key={agent.id} 
                className="hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => navigate(`/agents/${agent.id}`)}
                data-testid={`agent-card-${agent.id}`}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-slate-200 flex items-center justify-center text-slate-700 font-semibold text-lg">
                        {agent.nome?.[0]}{agent.cognome?.[0]}
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900">
                          {agent.nome} {agent.cognome}
                        </h3>
                        <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${
                          agent.attivo ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
                        }`}>
                          {agent.attivo ? 'Attivo' : 'Non attivo'}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2 mb-4">
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <Mail className="w-4 h-4 text-slate-400" />
                      {agent.email}
                    </div>
                    {agent.telefono && (
                      <div className="flex items-center gap-2 text-sm text-slate-600">
                        <Phone className="w-4 h-4 text-slate-400" />
                        {agent.telefono}
                      </div>
                    )}
                  </div>

                  {agentStats && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 pt-4 border-t">
                      <div className="text-center">
                        <p className="text-lg font-bold text-slate-900">{agentStats.lead_totali}</p>
                        <p className="text-xs text-slate-500">Lead</p>
                      </div>
                      <div className="text-center">
                        <p className="text-lg font-bold text-emerald-600">{agentStats.contratti_chiusi}</p>
                        <p className="text-xs text-slate-500">Contratti</p>
                      </div>
                      <div className="text-center">
                        <p className="text-lg font-bold text-sky-600">{agentStats.tasso_conversione}%</p>
                        <p className="text-xs text-slate-500">Conv.</p>
                      </div>
                    </div>
                  )}

                  <div className="mt-4 pt-4 border-t flex justify-between items-center">
                    <span className="text-xs text-slate-500">
                      Dal {formatDate(agent.created_at)}
                    </span>
                    <Button variant="ghost" size="sm">
                      <Eye className="w-4 h-4 mr-1" />
                      Dettagli
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>
    </div>
  );
};

export default Agents;
